import React from 'react';
import { fmtSignedPct } from './lib/format';
import { DIRECTION } from './lib/colors';

/**
 * Year-over-year change pill for Search Console.
 *
 * Deliberately NOT `shared/DeltaBadge` (§9-E): that component renders `—` for a null
 * value, which F9 forbids outright — when `comparison_available` is false there is no
 * comparison, and a dash or a `0%` reads as "flat" rather than "not measured". It also
 * has no text label, and colour alone is not an accessible signal for a metric whose
 * good direction is inverted. `DeltaBadge` is left untouched for its live Google Ads
 * and GA4 call sites.
 *
 * Renders NOTHING when `value` is null/undefined — that absence is the F9 behaviour.
 *
 * @param {number|null|undefined} value already-percent change, e.g. `22.4`
 * @param {'auto'|'neutral'} tone `'neutral'` renders the value with no colour and no
 *   arrow — used where the sign's meaning is not established (see `lib/position.js`).
 * @param {string} [caption] optional context, e.g. the comparison period
 */
const ChangeIndicator = ({ value, tone = 'auto', caption }) => {
  const text = fmtSignedPct(value);
  if (!text) return null;

  const n = Number(value);
  const neutral = tone === 'neutral' || n === 0;
  const improved = !neutral && n > 0;

  const color = neutral ? DIRECTION.neutral : improved ? DIRECTION.improved : DIRECTION.declined;
  // Glyph AND label, never colour alone (spec §9). The glyph is aria-hidden because
  // the adjacent word carries the same meaning to a screen reader.
  const glyph = neutral ? '' : improved ? '▲' : '▼';
  const label = neutral ? 'vs last year' : improved ? 'up vs last year' : 'down vs last year';

  return (
    <span className="inline-flex flex-wrap items-baseline gap-1 text-sm font-medium" style={{ color }}>
      {glyph && <span aria-hidden="true">{glyph}</span>}
      <span>{text}</span>
      <span className="text-xs font-normal">{label}</span>
      {caption && <span className="text-xs font-normal text-gray-400 dark:text-gray-500">{caption}</span>}
    </span>
  );
};

export default ChangeIndicator;
